import { Component, Element, Event, EventEmitter, h, Method, Prop, State, Watch } from '@stencil/core';
import { __ } from '@wordpress/i18n';
import { countryChoices, getCountryDetails } from '../../../functions/address';
import { reportChildrenValidity } from '../../../functions/form-data';
import { getCurrentUserCountryCode } from '../../../functions/google-maps';
import { Address, CountryLocaleFieldValue } from '../../../types';

const DEFAULT_COUNTRY_FIELDS: Array<CountryLocaleFieldValue> = [
  {
    name: 'country',
    priority: 30,
    label: __('Country', 'surecart'),
  },
  {
    name: 'name',
    priority: 40,
    label: __('Name or Company Name', 'surecart'),
  },
  {
    name: 'line_1',
    priority: 50,
    label: __('Address', 'surecart'),
  },
  {
    name: 'line_2',
    priority: 60,
    label: __('Line 2', 'surecart'),
  },
  {
    name: 'city',
    priority: 70,
    label: __('City', 'surecart'),
  },
  {
    name: 'state',
    priority: 80,
    label: __('State / County', 'surecart'),
  },
  {
    name: 'postal_code',
    priority: 90,
    label: __('Postal Code', 'surecart'),
  },
];

/**
 * @part base - The elements base wrapper.
 * @part input__base - The inputs base element.
 * @part select__base - The select boxes base element.
 * @part input - The html input element.
 * @part form-control - The form control wrapper.
 * @part label - The input label.
 * @part help-text - Help text that describes how to use the input.
 * @part trigger - The select box trigger.
 * @part panel - The select box panel.
 * @part caret - The select box caret.
 * @part search__base - The select search base.
 * @part search__input - The select search input.
 * @part search__form-control - The select search form control.
 * @part menu__base - The select menu base.
 * @part spinner__base  - The select spinner base.
 * @part empty - The select empty message.
 * @part block-ui - The block ui base component.
 * @part block-ui__content - The block ui content (spinner).
 */
@Component({
  tag: 'sc-address',
  styleUrl: 'sc-address.scss',
  shadow: true,
})
export class ScAddress {
  @Element() el: HTMLScAddressElement;

  /** The address. */
  @Prop({ mutable: true }) address: Partial<Address> = {
    country: null,
    city: null,
    line_1: null,
    line_2: null,
    postal_code: null,
    state: null,
  };

  @Prop() names: Partial<Address> = {
    name: 'shipping_name',
    country: 'shipping_country',
    city: 'shipping_city',
    line_1: 'shipping_line_1',
    line_2: 'shipping_line_2',
    postal_code: 'shipping_postal_code',
    state: 'shipping_state',
  };

  /** Is this loading?  */
  @Prop() loading: boolean = false;

  /** Is this disabled? */
  @Prop() disabled: boolean;

  /** The label for the field. */
  @Prop() label: string;

  /** Should we show name field? */
  @Prop({ reflect: true, mutable: true }) showName: boolean;

  /** Should we show name field? */
  @Prop() showLine2: boolean;

  /** Is this required? */
  @Prop({ reflect: true }) required: boolean = false;

  /** Is the name required */
  @Prop({ reflect: true }) requireName: boolean = false;

  /** Should we show the city field? */
  @State() showCity: boolean = true;

  /** Should we show the postal field? */
  @State() showPostal: boolean = true;

  /** Should we show the state field? */
  @State() showState: boolean = true;

  /** Country details. */
  @State() countryDetails: any = null;

  /** Holds our country choices. */
  @State() countryChoices: Array<{ value: string; label: string }>;

  /** Address change event. */
  @Event() scChangeAddress: EventEmitter<Partial<Address>>;

  /** Address change event. */
  @Event() scInputAddress: EventEmitter<Partial<Address>>;

  /** When the state changes, we want to update city and postal fields. */
  @Watch('address')
  async handleAddressChange() {
    if (!this.address?.country) return;
    if (!this.countryDetails || this.countryDetails?.code !== this.address.country) {
      this.countryDetails = await getCountryDetails(this.address.country);
    }
    this.scChangeAddress.emit(this.address);
    this.scInputAddress.emit(this.address);
  }

  @Watch('requireName')
  handleNameChange() {
    if (this.requireName) {
      this.showName = true;
    }
  }

  decodeHtmlEntities(html: string) {
    return new DOMParser().parseFromString(html, 'text/html')?.body.textContent || html;
  }

  updateAddress(address: Partial<Address>) {
    this.address = { ...this.address, ...address };
  }

  handleAddressInput(address: Partial<Address>) {
    this.scInputAddress.emit({ ...this.address, ...address });
  }

  clearAddress() {
    // Only reset fields whose format depends on country (region list, postal code pattern).
    // Keep line_1/line_2 — they're free text, and clearing them silently discards what the buyer already typed.
    this.address = {
      name: this.address?.name,
      line_1: this.address?.line_1,
      line_2: this.address?.line_2,
      country: null,
      city: null,
      postal_code: null,
      state: null,
    };
  }

  componentWillLoad() {
    // Start collapsed when Google Maps is active — the child component
    // will emit show/hide events to control this going forward.
    if (window?.scData?.google_map_api_key) {
      this.toggleAddressFieldsVisibility(false);
    }

    this.initCountryChoices();
    this.handleAddressChange();
    this.handleNameChange();
    this.fetchUserCountry();
  }

  async fetchUserCountry() {
    if (this.address?.country) {
      return;
    }

    const countryCode = await getCurrentUserCountryCode();
    if (countryCode) {
      this.updateAddress({ country: countryCode });
    }
  }

  @Method()
  async initCountryChoices() {
    this.countryChoices = await countryChoices();
    const country = this.countryChoices?.find(country => country.value === this.address?.country)?.value || null;
    this.updateAddress({ country });
  }

  @Method()
  async reportValidity() {
    // Collapsed city/state/postal fields render as not-required (see isFieldIncluded/render) so they don't
    // block submission while hidden. Expand them first so a required address can't be bypassed by leaving
    // line_1 empty (fields never having shown) or clearing it after the fact.
    if (this.required && !this.isFieldsExpanded()) {
      this.toggleAddressFieldsVisibility(true);
      await new Promise(resolve => requestAnimationFrame(resolve));
    }

    return reportChildrenValidity(this.el);
  }

  sortedFields() {
    if (!this.countryDetails || !this?.address?.country) {
      return DEFAULT_COUNTRY_FIELDS;
    }

    return (this?.countryDetails?.address_formats?.edit
      ?.match(/{{([^}]+)}}/g)
      .map(match => match.slice(2, -2))
      .map(field => ({
        name: field,
        label: this?.countryDetails?.address_labels?.[field] || DEFAULT_COUNTRY_FIELDS?.find(defaultField => defaultField?.name === field)?.label,
      })) || []) as CountryLocaleFieldValue[];
  }

  regions() {
    let regions =
      this?.countryDetails?.states?.map(state => ({
        value: state?.code,
        label: state?.name,
      })) || [];

    if (window?.wp?.hooks?.applyFilters) {
      regions = window.wp.hooks.applyFilters('surecart_address_regions', regions, this?.address?.country) as Array<{ value: string; label: string }>;
    }

    return regions as Array<{ value: string; label: string }>;
  }

  toggleAddressFieldsVisibility(show: boolean) {
    this.showCity = show;
    this.showPostal = show;
    this.showState = show;
  }

  getRoundedProps(index: number, length: number) {
    const isFirst = index === 0;
    const isLast = index === length - 1;

    return {
      squaredTop: isLast,
      squaredBottom: isFirst,
      squared: !isLast && !isFirst,
    };
  }

  /** Whether the collapsible fields are expanded.
   *  When Google Maps is off, showCity stays at its default (true) and fields are always expanded.
   *  When Google Maps is on, the child toggles this via show/hide events. */
  isFieldsExpanded(): boolean {
    return this.showCity;
  }

  /** Names of fields that collapse when Google Maps autocomplete is active. */
  private collapsibleFieldNames = ['line_2', 'city', 'state', 'postal_code'];

  /** Whether a field should be included in the render. */
  isFieldIncluded(field: CountryLocaleFieldValue): boolean {
    switch (field.name) {
      case 'name':
        return !!this.showName;
      case 'state':
        return !!this?.regions()?.length && !!this?.address?.country;
      case 'line_2':
        // When collapsed, always include so browser autocomplete can fill it (CSS hides it).
        // When expanded, respect the showLine2 prop from settings.
        if (!this.isFieldsExpanded()) return true;
        return this.showLine2 || !!this?.address?.line_2?.length;
      default:
        return true;
    }
  }

  renderField(field: CountryLocaleFieldValue, roundedProps: any, isRequired: boolean) {
    switch (field.name) {
      case 'country':
        return (
          <sc-select
            exportparts="base:select__base, input, form-control, label, help-text, trigger, panel, caret, search__base, search__input, search__form-control, menu__base, spinner__base, empty"
            part="name__input"
            value={this.address?.country ?? ''}
            onScChange={(e: any) => {
              if (e.target.value === this.address?.country) return;
              this.clearAddress();
              this.updateAddress({ country: e.target.value });
            }}
            choices={this.countryChoices}
            autocomplete={'country-name'}
            placeholder={field.label}
            name={this.names?.country}
            search
            unselect={false}
            disabled={this.disabled}
            required={isRequired}
            aria-label={field.label}
            {...roundedProps}
          />
        );

      case 'name':
        return (
          <sc-input
            exportparts="base:input__base, input, form-control, label, help-text"
            value={this?.address?.name ?? ''}
            onScChange={(e: any) => this.updateAddress({ name: e.target.value || null })}
            onScInput={(e: any) => this.handleAddressInput({ name: e.target.value || null })}
            autocomplete="name"
            placeholder={field.label}
            name={this.names?.name}
            disabled={this.disabled}
            required={this.requireName}
            aria-label={field.label}
            {...roundedProps}
          />
        );

      case 'line_1':
        return (
          <sc-address-suggestions
            address={this.address}
            names={this.names}
            regions={this.regions()}
            label={field.label}
            disabled={this.disabled}
            required={isRequired}
            inputProps={roundedProps}
            onScChangeAddress={(e: any) => this.updateAddress(e.detail)}
            onScShowAddressFields={() => this.toggleAddressFieldsVisibility(true)}
            onScHideAddressFields={() => this.toggleAddressFieldsVisibility(false)}
          />
        );

      case 'line_2':
        return (
          <sc-input
            exportparts="base:input__base, input, form-control, label, help-text"
            value={this?.address?.line_2 ?? ''}
            onScChange={(e: any) => this.updateAddress({ line_2: e.target.value || null })}
            onScInput={(e: any) => this.handleAddressInput({ line_2: e.target.value || null })}
            autocomplete="address-line2"
            placeholder={field.label}
            name={this.names?.line_2}
            disabled={this.disabled}
            aria-label={field.label}
            {...roundedProps}
          />
        );

      case 'city':
        return (
          <sc-input
            exportparts="base:input__base, input, form-control, label, help-text"
            placeholder={field.label}
            name={this.names?.city}
            value={this?.address?.city ?? ''}
            onScChange={(e: any) => this.updateAddress({ city: e.target.value || null })}
            onScInput={(e: any) => this.handleAddressInput({ city: e.target.value || null })}
            autocomplete={'address-level2'}
            required={isRequired}
            disabled={this.disabled}
            aria-label={field.label}
            {...roundedProps}
          />
        );

      case 'state':
        return (
          <sc-select
            exportparts="base:select__base, input, form-control, label, help-text, trigger, panel, caret, search__base, search__input, search__form-control, menu__base, spinner__base, empty"
            placeholder={field.label}
            name={this.names?.state}
            autocomplete={'address-level1'}
            value={this?.address?.state ?? ''}
            onScChange={(e: any) => this.updateAddress({ state: e.target.value || e.detail?.value || null })}
            choices={this.regions()}
            required={isRequired}
            disabled={this.disabled}
            search
            aria-label={field.label}
            {...roundedProps}
          />
        );

      case 'postal_code':
        return (
          <sc-input
            exportparts="base:input__base, input, form-control, label, help-text"
            placeholder={field.label}
            name={this.names?.postal_code}
            onScChange={(e: any) => this.updateAddress({ postal_code: e.target.value || null })}
            onScInput={(e: any) => this.handleAddressInput({ postal_code: e.target.value || null })}
            autocomplete={'postal-code'}
            required={isRequired}
            value={this?.address?.postal_code ?? ''}
            disabled={this.disabled}
            maxlength={this.address?.country === 'US' ? 5 : undefined}
            pattern={this.countryDetails?.postal_code_regex || undefined}
            customValidity={this.countryDetails?.postal_code_regex ? __('Please enter a valid postal code', 'surecart') : undefined}
            aria-label={field.label}
            {...roundedProps}
          />
        );

      default:
        return null;
    }
  }

  render() {
    const allFields = (this.sortedFields() ?? []).filter(field => this.isFieldIncluded(field));
    const isExpanded = this.isFieldsExpanded();

    // Split fields into always-visible (country, name, line_1) and collapsible (line_2, city, state, postal_code).
    const topFields = allFields.filter(f => !this.collapsibleFieldNames.includes(f.name));
    const bottomFields = allFields.filter(f => this.collapsibleFieldNames.includes(f.name));

    // Compute rounded props based on visible field count.
    const totalForRounding = isExpanded ? allFields.length : topFields.length;

    return (
      <div class="sc-address" part="base">
        <sc-form-control label={this.label} exportparts="label, help-text, form-control" class="sc-address__control" required={this.required}>
          {topFields.map((field: any, index: number) => {
            const roundedProps = this.getRoundedProps(index, totalForRounding);
            return this.renderField(field, roundedProps, this.required);
          })}

          <div
            class={{
              'sc-address__collapsible': true,
              'sc-address__collapsible--expanded': isExpanded,
            }}
            aria-hidden={!isExpanded ? 'true' : 'false'}
          >
            {bottomFields.map((field: any, index: number) => {
              const globalIndex = topFields.length + index;
              const roundedProps = this.getRoundedProps(globalIndex, allFields.length);
              // Don't require fields when collapsed — prevents hidden required validation errors.
              const isRequired = this.required && isExpanded;
              return this.renderField(field, roundedProps, isRequired);
            })}
          </div>
        </sc-form-control>

        {this.loading && <sc-block-ui exportparts="base:block-ui, content:block-ui__content"></sc-block-ui>}
      </div>
    );
  }
}
